/* eslint-disable no-restricted-properties */
import { useEffect, useState, useCallback } from 'react'
import { useWebsocketConnection } from 'providers/WebsocketProvider'
import { ReloadPrompt } from './ReloadPrompt'
import { useModals } from 'almanac-ui/Modal'
import { getBuildId } from 'utils/getBuildId'

const IDLE_TIMEOUT = 60 * 1000 // 1 minute

interface Data {
  action: 'reload' | 'prompt'
  build_id: number | undefined
}

const ForceReloadListener = () => {
  const websocketConnection = useWebsocketConnection()
  const [lastActivity, setLastActivity] = useState<number | null>(null)
  const [pendingReload, setPendingReload] = useState(false)
  const [showPrompt, setShowPrompt] = useState(false)
  const { openModal } = useModals()

  const resetActivityTimer = useCallback(() => {
    setLastActivity(Date.now())
  }, [])

  useEffect(() => {
    // Set up event listeners for user activity
    const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll']
    activityEvents.forEach((event) => window.addEventListener(event, resetActivityTimer))

    return () => {
      activityEvents.forEach((event) => window.removeEventListener(event, resetActivityTimer))
    }
  }, [resetActivityTimer])

  useEffect(() => {
    if (!websocketConnection) return

    const handleWebsocketMessage = async (data: Data) => {
      if (!data.build_id || data.build_id > getBuildId()) {
        setPendingReload(true)
        setShowPrompt(data.action === 'prompt')
      }
    }

    const subscription = websocketConnection.subscriptions.create(
      { channel: 'ForceReloadChannel' },
      { received: handleWebsocketMessage },
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [websocketConnection])

  useEffect(() => {
    if (!pendingReload) return

    const clearCache = async () => {
      if (window.caches) {
        const keys = await window.caches.keys()
        keys.forEach((cacheName) => {
          window.caches.delete(cacheName)
        })
      }
    }

    const reload = () => {
      const currentUrl = new URL(window.location.href)
      currentUrl.searchParams.set('updated', 'true')
      window.location.href = currentUrl.toString()
    }

    const checkIdleAndReload = async () => {
      const currentTime = Date.now()

      if (!lastActivity || currentTime - lastActivity >= IDLE_TIMEOUT) {
        // User has been idle for the specified time, perform the reload
        if (showPrompt) {
          openModal(ReloadPrompt, {
            onReload: async () => {
              await clearCache()
              reload()
            },
          })
        } else {
          await clearCache()
          reload()
        }
      }
    }

    checkIdleAndReload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingReload, lastActivity, showPrompt])

  return null
}

export default ForceReloadListener
